import { useParams, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Send, Lock, PlayCircle, CheckCircle2, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { isStaff as roleIsStaff } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import AttachmentUploader, { AttachmentGallery } from "@/components/AttachmentUploader";

function fmt(iso) { try { return new Date(iso).toLocaleString("fr-FR"); } catch { return ""; } }

const STATUS = {
  open: { label: "Ouvert", cls: "bg-primary/15 text-primary border-primary/40", icon: PlayCircle },
  in_progress: { label: "En cours", cls: "bg-yellow-500/15 text-yellow-500 border-yellow-500/40", icon: PlayCircle },
  closed: { label: "Fermé", cls: "bg-muted text-muted-foreground border-border", icon: Lock },
};

export default function TicketDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isStaff = roleIsStaff(user);
  const [reply, setReply] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);

  const { data: ticket, isLoading, error, refetch } = useQuery({
    queryKey: ["ticket", id],
    queryFn: () => api.get(`/tickets/${id}`).then((r) => r.data),
    retry: false,
    enabled: !!user,
  });

  const send = async () => {
    if (reply.trim().length < 1 && replyAttachments.length === 0) return;
    try {
      await api.post(`/tickets/${id}/messages`, {
        content: reply.trim() || "(pièce jointe)",
        attachments: replyAttachments.map((a) => a.url),
      });
      setReply("");
      setReplyAttachments([]);
      toast.success("Message envoyé");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const setStatus = async (status) => {
    try {
      await api.post(`/tickets/${id}/status`, { status });
      toast.success("Statut mis à jour");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const deleteTicket = async () => {
    if (!window.confirm("Supprimer définitivement ce ticket et toute sa conversation ? Cette action est irréversible.")) return;
    try {
      await api.delete(`/tickets/${id}`);
      toast.success("Ticket supprimé");
      nav("/admin");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  if (isLoading) return <div className="py-24 text-center text-muted-foreground">Chargement...</div>;
  if (error) {
    const status = error?.response?.status;
    return (
      <div className="max-w-lg mx-auto py-16 px-4 text-center">
        <p className="font-display uppercase text-xl mb-2">{status === 403 ? "Accès refusé" : "Ticket introuvable"}</p>
        <Button asChild variant="outline" className="mt-3"><Link to="/tickets">Retour aux tickets</Link></Button>
      </div>
    );
  }
  if (!ticket) return null;

  const st = STATUS[ticket.status] || STATUS.open;
  const StatusIcon = st.icon;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Button variant="ghost" size="sm" onClick={() => nav(-1)} className="mb-4"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge variant="outline" className="font-mono-tech uppercase text-[10px]">{ticket.type_label}</Badge>
            <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${st.cls}`}>
              <StatusIcon className="h-3 w-3" /> {st.label}
            </span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl uppercase tracking-tight">{ticket.subject}</h1>
          <p className="text-xs text-muted-foreground mt-1">Ouvert le {fmt(ticket.created_at)} par {ticket.author.persona_name}</p>
        </div>
        {isStaff && (
          <div className="flex gap-2">
            {ticket.status !== "in_progress" && (
              <Button size="sm" variant="outline" onClick={() => setStatus("in_progress")} data-testid="ticket-in-progress">En cours</Button>
            )}
            {ticket.status !== "closed" ? (
              <Button size="sm" variant="outline" onClick={() => setStatus("closed")} data-testid="ticket-close"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Clore</Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setStatus("open")} data-testid="ticket-reopen">Rouvrir</Button>
            )}
            <Button size="sm" variant="outline" onClick={deleteTicket} className="text-destructive hover:bg-destructive/10" data-testid="ticket-delete">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Supprimer
            </Button>
          </div>
        )}
      </div>

      {/* Details */}
      <Card className="mb-6">
        <CardContent className="p-5">
          <p className="font-display uppercase text-xs tracking-widest text-muted-foreground mb-3">Détails du ticket</p>
          <dl className="grid sm:grid-cols-2 gap-3">
            {Object.entries(ticket.fields || {}).map(([k, v]) => (
              <div key={k}>
                <dt className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-tech">{k}</dt>
                <dd className="text-sm whitespace-pre-wrap break-words">{String(v) || <span className="text-muted-foreground">—</span>}</dd>
              </div>
            ))}
          </dl>
          {ticket.attachments && ticket.attachments.length > 0 && (
            <div className="mt-4">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-tech mb-2">Pièces jointes</p>
              <AttachmentGallery attachments={ticket.attachments} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversation */}
      <p className="font-display uppercase text-sm tracking-widest text-muted-foreground mb-3">Conversation</p>
      <div className="space-y-3 mb-6">
        {(ticket.messages || []).length === 0 && (
          <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">Aucun message pour le moment.</CardContent></Card>
        )}
        {(ticket.messages || []).map((m) => (
          <Card key={m.id}>
            <CardContent className="p-4 grid grid-cols-[auto_1fr] gap-3">
              <Avatar className="h-9 w-9">
                <AvatarImage src={m.author.avatar_url} />
                <AvatarFallback>{m.author.persona_name?.[0]}</AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium">{m.author.persona_name}</span>
                  <span className="text-[10px] uppercase tracking-widest text-primary font-mono-tech">{m.author.role}</span>
                  <span className="text-xs text-muted-foreground">· {fmt(m.created_at)}</span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                <AttachmentGallery attachments={m.attachments} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Reply */}
      {ticket.status === "closed" && !isStaff ? (
        <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">Ce ticket est fermé.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-4 space-y-3">
            <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={4} placeholder="Votre message..." data-testid="ticket-reply-textarea" />
            <AttachmentUploader value={replyAttachments} onChange={setReplyAttachments} />
            <div className="flex justify-end">
              <Button onClick={send} disabled={reply.trim().length < 1 && replyAttachments.length === 0} data-testid="ticket-reply-submit"><Send className="h-3.5 w-3.5 mr-1" /> Envoyer</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
